import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { IngredientClient } from '../../../infrastructure/IngredientClient';
import { getConfig } from '../../../config';
import { AdminApiDomain, getAdminApiUrl } from '../../../config/ApiRouting';
import type {
  Ingredient,
  CreateIngredientRequest,
  UpdateIngredientRequest,
  GetIngredientsRequest,
  IngredientsResponse,
} from '../../../models/Ingredient';

const config = getConfig();
const ingredientClient = new IngredientClient(getAdminApiUrl(AdminApiDomain.INGREDIENT), config.apiKey);

const QUERY_KEYS = {
  ingredients: ['ingredients'] as const,
  ingredient: (name: string, version?: number) => ['ingredient', name, version] as const,
};

export function useIngredient() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Fetch all ingredients
  const ingredientsQuery = useQuery({
    queryKey: QUERY_KEYS.ingredients,
    queryFn: async (): Promise<IngredientsResponse> => {
      try {
        setError(null);
        return await ingredientClient.getIngredients();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch ingredients';
        setError(errorMessage);
        throw err;
      }
    },
  });

  // Fetch single ingredient
  const useIngredientDetail = (ingredientName: string, version?: number) => {
    return useQuery({
      queryKey: QUERY_KEYS.ingredient(ingredientName, version),
      queryFn: async (): Promise<Ingredient> => {
        try {
          setError(null);
          return await ingredientClient.getIngredient(ingredientName, version);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to fetch ingredient';
          setError(errorMessage);
          throw err;
        }
      },
      enabled: Boolean(ingredientName),
    });
  };

  // Create ingredient mutation
  const createIngredientMutation = useMutation({
    mutationFn: async (request: CreateIngredientRequest): Promise<Ingredient> => {
      try {
        setError(null);
        return await ingredientClient.createIngredient(request);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create ingredient';
        setError(errorMessage);
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ingredients });
    },
  });

  // Update ingredient mutation
  const updateIngredientMutation = useMutation({
    mutationFn: async ({
      ingredientName,
      request,
    }: {
      ingredientName: string;
      request: UpdateIngredientRequest;
    }): Promise<Ingredient> => {
      try {
        setError(null);
        return await ingredientClient.updateIngredient(ingredientName, request);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update ingredient';
        setError(errorMessage);
        throw err;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ingredients });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ingredient(data.ingredientName) });
    },
  });

  // Delete ingredient mutation
  const deleteIngredientMutation = useMutation({
    mutationFn: async (ingredientName: string): Promise<void> => {
      try {
        setError(null);
        return await ingredientClient.deleteIngredient(ingredientName);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete ingredient';
        setError(errorMessage);
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ingredients });
    },
  });

  // Search ingredients by names
  const searchIngredientsMutation = useMutation({
    mutationFn: async (request: GetIngredientsRequest): Promise<IngredientsResponse> => {
      try {
        setError(null);
        return await ingredientClient.getIngredientsByNames(request);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to search ingredients';
        setError(errorMessage);
        throw err;
      }
    },
  });

  // Bulk create ingredients
  const bulkCreateIngredientsMutation = useMutation({
    mutationFn: async (ingredients: CreateIngredientRequest[]) => {
      try {
        setError(null);
        return await ingredientClient.bulkCreateIngredients(ingredients);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to bulk create ingredients';
        setError(errorMessage);
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ingredients });
    },
  });

  return {
    // Queries
    ingredientsQuery,
    useIngredientDetail,

    // Mutations
    createIngredient: createIngredientMutation.mutateAsync,
    updateIngredient: updateIngredientMutation.mutateAsync,
    deleteIngredient: deleteIngredientMutation.mutateAsync,
    searchIngredients: searchIngredientsMutation.mutateAsync,
    bulkCreateIngredients: bulkCreateIngredientsMutation.mutateAsync,

    // Loading states
    isCreating: createIngredientMutation.isPending,
    isUpdating: updateIngredientMutation.isPending,
    isDeleting: deleteIngredientMutation.isPending,
    isSearching: searchIngredientsMutation.isPending,
    isBulkCreating: bulkCreateIngredientsMutation.isPending,

    // Error state
    error,
    setError,
  };
}
